import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import type { OrganizationArchive, OrganizationArchiveStatus } from "@/types/api"
import { formatInstantDateTime, formatIsoDateFr, shiftIsoDay } from "@/lib/organisation-day"

// Affichage de la résiliation datée et de l'export.
// Date d'effet : de demain à dans un an.
// Effacement définitif : 60 jours après la date d'effet.
// Dates en JJ/MM/AAAA, comme les e-mails.

/** Clés de cache, invalidées par les mutations de ces écrans. */
export const organizationTerminationKeys = {
  termination: ["organization-termination"] as const,
  archive: ["organization-archive"] as const,
}

/** Délai entre la date d'effet et l'effacement définitif. */
export const PURGE_DELAY_DAYS = 60

/** Durée de téléchargement d'un export sans résiliation en cours. */
export const ARCHIVE_RETENTION_DAYS = 30

/** Rafraîchissement de l'export tant qu'il se prépare. */
export const ARCHIVE_POLL_MS = 5_000

// Jour `AAAA-MM-JJ` : capture l'année, le mois et le jour.
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

/** « 10 octobre 2026 à 14:05 ». */
const LONG_DAY_AND_MINUTE: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }

function pad(value: number): string {
  return String(value).padStart(2, "0")
}

/** Préavis maximal en jours, celui du serveur (`MAX_NOTICE_DAYS`). */
const MAX_NOTICE_DAYS = 365

/**
 * Bornes de la date d'effet, autour de `today`.
 * `today` : le jour de l'organisme, servi par l'API.
 * De demain à 365 jours, comme le serveur.
 * Le navigateur peut être à la veille ou au lendemain.
 *
 * @example terminationDateBounds("2026-10-10") // { min: "2026-10-11", max: "2027-10-10" }
 */
export function terminationDateBounds(today: string): { min: string; max: string } {
  return pipe(
    parseIsoDay(today),
    O.match(
      () => ({ min: today, max: today }),
      (day) => ({
        min: isoDayOf(new Date(day.getTime() + DAY_MS)),
        max: isoDayOf(new Date(day.getTime() + MAX_NOTICE_DAYS * DAY_MS)),
      }),
    ),
  )
}

/** Jour `AAAA-MM-JJ` d'une date UTC à minuit. */
function isoDayOf(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** Année, mois (1 à 12) et jour lus dans une date `AAAA-MM-JJ`. */
type DayParts = { year: number; month: number; day: number }

function dayParts([, year, month, day]: RegExpExecArray): DayParts {
  return { year: Number(year), month: Number(month), day: Number(day) }
}

function utcDate(parts: DayParts): Date {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day))
}

// Date.UTC reporte un jour hors du mois. Le 31 février devient mars.
function isRealDay(parts: DayParts): boolean {
  const parsed = utcDate(parts)
  const sameYear = parsed.getUTCFullYear() === parts.year
  const sameMonth = parsed.getUTCMonth() === parts.month - 1
  const sameDay = parsed.getUTCDate() === parts.day
  return sameYear && sameMonth && sameDay
}

/** Date `AAAA-MM-JJ` réelle (le 31 février est refusé). */
function parseIsoDay(value: string): O.Option<Date> {
  return pipe(O.fromNullable(ISO_DAY.exec(value)), O.map(dayParts), O.filter(isRealDay), O.map(utcDate))
}

/**
 * Date d'effet retenue : bien formée et comprise dans les bornes.
 *
 * @example validEffectiveOn("2026-12-31", "2026-10-10") // O.some("2026-12-31")
 */
export function validEffectiveOn(value: string, today: string): O.Option<string> {
  const { min, max } = terminationDateBounds(today)
  return pipe(
    parseIsoDay(value),
    O.map(() => value),
    O.filter((day) => day >= min && day <= max),
  )
}

/**
 * « 10/10/2026 » pour une date `AAAA-MM-JJ`.
 *
 * @example formatIsoDay("2026-10-10") // "10/10/2026"
 */
export function formatIsoDay(value: string): string {
  return pipe(
    parseIsoDay(value),
    O.match(
      () => value,
      () => formatIsoDateFr(value),
    ),
  )
}

/**
 * Instant ISO dans le fuseau de l'organisme : « 10 octobre 2026 à 14:05 ».
 *
 * @example formatInstant("2026-10-10T12:05:00Z", "Europe/Paris") // "10 octobre 2026 à 14:05"
 */
export function formatInstant(value: string, timeZone: string): string {
  return formatInstantDateTime(value, timeZone, LONG_DAY_AND_MINUTE)
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Jour `AAAA-MM-JJ` décalé de `days`, écrit JJ/MM/AAAA. */
function shiftedDay(isoDay: string, days: number): string {
  return pipe(
    parseIsoDay(isoDay),
    O.match(
      () => isoDay,
      () => formatIsoDateFr(shiftIsoDay(isoDay, days)),
    ),
  )
}

/**
 * Jour de l'effacement définitif d'une résiliation au `effectiveOn`.
 *
 * @example purgeDayOf("2026-10-10") // "09/12/2026"
 */
export function purgeDayOf(effectiveOn: string): string {
  return shiftedDay(effectiveOn, PURGE_DELAY_DAYS)
}

/**
 * Veille de la date d'effet : dernier jour d'accès et d'annulation.
 *
 * @example lastAccessDayOf("2026-10-10") // "09/10/2026"
 */
export function lastAccessDayOf(effectiveOn: string): string {
  return shiftedDay(effectiveOn, -1)
}

/**
 * Phrase de fermeture : date d'effet, puis dernier jour d'accès.
 *
 * @example closingSentence("2026-10-10") // "à partir du 10/10/2026 à 0 h 00 (heure de l'organisme) ; le dernier jour d'accès est le 09/10/2026"
 */
export function closingSentence(effectiveOn: string): string {
  return `à partir du ${formatIsoDay(effectiveOn)} à 0 h 00 (heure de l'organisme) ; le dernier jour d'accès est le ${lastAccessDayOf(effectiveOn)}`
}

/** Ce qui est effacé, sans jamais dire « tout est effacé ». */
export const ERASED_SCOPE =
  "Toutes les données, tous les fichiers et toutes les demandes de signature DocuSeal de l'organisme (avec leurs modèles) sont effacés, ainsi que les comptes utilisateurs qui n'appartiennent à aucun autre organisme (hors organisme de démonstration). Seul subsiste un journal de l'effacement."

/** Ce qui s'arrête à la fermeture. */
export const CLOSURE_SCOPE =
  "Les autres membres de l'organisme n'ont plus accès à l'espace. Les portails (apprenants, formateurs, jurys), les liens publics (devis, émargement, questionnaires, signatures en cours) et les connexions MCP cessent de fonctionner, et les envois automatiques de l'organisme s'arrêtent. Seul le lien de désinscription des e-mails reste ouvert. Informez vos apprenants, formateurs et clients avant cette date."

/** Les trois e-mails reçus par chaque administrateur. */
export const ADMIN_NOTICES =
  "Chaque administrateur reçoit 3 e-mails, envoyés à l'enregistrement de la résiliation, à la fermeture de l'espace et 7 jours avant l'effacement."

/**
 * Un effacement demandé retire l'export déjà produit.
 * L'export recopie les données effacées.
 */
export const ARCHIVE_WITHDRAWN_ON_ERASURE =
  "Tout effacement demandé (contact, formateur, membre de jury, données de santé) retire aussi l'export déjà produit, qui les recopie : demandez-en alors un nouveau."

/** Export d'un organisme en résiliation : jusqu'à l'effacement. */
export const ARCHIVE_UNTIL_ERASURE =
  "Un export se télécharge jusqu'à l'effacement des données. Demandez-le sans attendre, car il ne peut plus être demandé une fois l'effacement commencé."

const ARCHIVE_STATUS_LABELS: Record<OrganizationArchiveStatus, string> = {
  pending: "En attente",
  processing: "En préparation",
  completed: "Prêt",
  failed: "Échec",
  expired: "Expiré",
}

export function archiveStatusLabel(status: OrganizationArchiveStatus): string {
  return ARCHIVE_STATUS_LABELS[status]
}

/**
 * L'export se prépare encore : la page se rafraîchit d'elle-même.
 *
 * @example isArchiveInFlight({ ...archive, status: "processing" }) // true
 */
export function isArchiveInFlight(archive: OrganizationArchive): boolean {
  return archive.status === "pending" || archive.status === "processing"
}
